import { Stack, Typography } from '@mui/material';
import get from 'lodash-es/get';
import React, { useRef,useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useConstants } from '../../../../hooks/useConstants';
import { useSnackBar } from '../../../../hooks/useSnackBar';
import TextField from '../../../forms/TextField';
import CaptureControls from './AutoTag/CaptureControls';
import IframePreview from './AutoTag/IframePreview';
import ScriptDetector from './AutoTag/ScriptDetector';
import Link from './Link';
import PreviewControls from './Link/PreviewControls';

interface AutoTagProps {
 onImageUpdate: (imageUrl: string | null) => void;
}

export default function AutoTag({ onImageUpdate }: AutoTagProps) {
 const { watch, setValue } = useFormContext();
 const { showSnackBar } = useSnackBar();
 const { constants } = useConstants();
 const iframeRef = useRef<HTMLIFrameElement>(null);
 
 const [isCapturing, setIsCapturing] = useState(false);
 const [captureError, setCaptureError] = useState<string | null>(null);

  const watchedTag = watch('autoTag');
  const watchedLink = watch('link');
  const campaignId = watch('campaignId') || watch('adGroupId');
  const creativeId = watch('id');
 const selectedSize = watch('size');
 const uploadType = watch('uploadType');

 const showUserUpload = uploadType === 'user' && watchedLink;
 const showIframePreview = Boolean(watchedTag);

 const handleCapture = async () => {
   if (!watchedTag || !selectedSize) {
     showSnackBar('Missing required fields', 'error', {
       event: 'creative_capture_error',
       creative_id: creativeId,
       campaign_id: campaignId,
     });
     return;
   }

   try {
     setIsCapturing(true);
     setCaptureError(null);

     const S3_DIRECT_UPLOAD_URL = get(constants, 'S3_DIRECT_UPLOAD_URL') || 'https://s3-external-1.amazonaws.com';
     const STAGE = get(constants, 'RAILS.STAGE') || 'production';

     const response = await fetch('https://n0ckdf0gnb.execute-api.us-east-1.amazonaws.com/dev/screenshot', {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({ 
         htmlTag: watchedTag, 
         size: selectedSize,
         s3DirectUploadUrl: S3_DIRECT_UPLOAD_URL,
         stage: STAGE
       }),
     });

     const data = await response.json();
     if (!response.ok || !data.success) throw new Error(data.error || 'Failed to capture');

     setValue('link', data.imageUrl, { shouldDirty: true });
     setValue('uploadType', 'capture', { shouldDirty: true });
     onImageUpdate(data.imageUrl);
     showSnackBar('Successfully captured preview', 'success', {
       event: 'creative_captured',
       creative_id: creativeId,
       campaign_id: campaignId,
     });

   } catch (error) {
     const message = error instanceof Error ? error.message : 'Failed to capture';
     setCaptureError(message);
     showSnackBar(`Capture failed: ${message}`, 'error', {
       event: 'creative_capture_error',
       creative_id: creativeId,
       campaign_id: campaignId,
     });
   } finally {
     setIsCapturing(false);
   }
 };

 const handleRemove = () => {
   setValue('link', null, { shouldDirty: true });
   setValue('uploadType', null, { shouldDirty: true });
   onImageUpdate(null);
 };

 return (
  // eslint-disable-next-line jsx-a11y/anchor-is-valid
   <Stack spacing={2}>
     <Typography variant="caption">Preview</Typography>

     {showUserUpload ? (
       // eslint-disable-next-line jsx-a11y/anchor-is-valid
       <Link
         fieldName="link"
         s3InfoUrl="/admin/helpers/s3_upload_info/"
         accept=".png,.jpg,.jpeg,.gif"
         onSuccess={(url) => {
           setValue('uploadType', 'user', { shouldDirty: true });
           setValue('link', url, { shouldDirty: true });
           onImageUpdate(url);
         }}
         onRemoveSuccess={handleRemove}
       />
     ) : (
       <>
         {!watchedLink && (
           // eslint-disable-next-line jsx-a11y/anchor-is-valid
           <Link
             fieldName="link"
             s3InfoUrl="/admin/helpers/s3_upload_info/"
             accept=".png,.jpg,.jpeg,.gif"
             onSuccess={(url) => {
               setValue('uploadType', 'user', { shouldDirty: true });
               setValue('link', url, { shouldDirty: true });
               onImageUpdate(url);
             }}
             onRemoveSuccess={handleRemove}
           />
         )}

         {showIframePreview && (
           <Stack spacing={2}>
             <IframePreview
               watchedTag={watchedTag}
               selectedSize={selectedSize}
               iframeRef={iframeRef}
               showSnackBar={showSnackBar}
             />
             {!watchedLink && (
               <CaptureControls
                 isCapturing={isCapturing}
                 error={captureError}
                 selectedSize={selectedSize}
                 onCapture={handleCapture}
               />
             )}
           </Stack>
         )}
       </>
     )}

    {watchedLink && uploadType !== 'user' && (
      <PreviewControls
        url={watchedLink}
        onRemove={handleRemove}
        campaignId={campaignId || undefined}
        creativeId={creativeId || undefined}
      />
    )}

     <TextField
       name="autoTag"
       label="Custom HTML (Optional)"
       multiline
       rows={5}
       value={watchedTag || ''}
       onChange={(e) => setValue('autoTag', e.target.value, { shouldDirty: true })}
       variant="standard"
       sx={{
         '& .MuiInputBase-root': {
           resize: 'vertical',
           bgcolor: 'transparent',
           '& fieldset': {
             borderColor: 'transparent'
           },
           '&:hover fieldset': {
             borderColor: 'transparent'
           },
           '&.Mui-focused fieldset': {
             borderColor: 'transparent'
           }
         },
         '& .MuiInputBase-inputMultiline': {
           overflow: 'auto'
         }
       }}
     />
      
      <Stack direction="row" sx={{ justifyContent: 'space-between' }} spacing={1}>
        <Stack direction="row" alignItems="center" sx={{ justifyContent: 'flex-start' }}>
        {watchedTag && <ScriptDetector iframeRef={iframeRef} tag={watchedTag} />}
        </Stack>
        <Stack direction="row" alignItems="center" sx={{ justifyContent: 'flex-end' }} />
        <Typography variant="caption">
    
          <a
            href="https://github.com/ThinkNear/EngineeringWiki/wiki/Common-errors-in-XHTML-validation/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Common Errors
          </a>
        </Typography>
      </Stack>
    </Stack>
  );
}